import type { ReactNode } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { motion, useReducedMotion } from 'framer-motion';
import { LogOutIcon } from 'lucide-react';
import { useAskSarahActions, useSignedInAccount } from '../../contexts/AccountContext';
import { founder } from '../../data/content';
import { ease } from '../../core/format/motion';
import { VerifyEmailBanner } from './VerifyEmailBanner';
import { PaymentIssueBanner } from './PaymentIssueBanner';
import { useAccountNav } from './useAccountNav';

/**
 * Zijmenu op de computer, duimbalk onderin op de telefoon: dezelfde vier tabs (useAccountNav).
 * Een subpagina (Bestellingen, Dagboek) laat de tab van haar ouder oplichten.
 * Uitloggen staat op de computer onderaan het zijmenu, op de telefoon onderaan Ik.
 */
export function AccountLayout({
  children
}: {
  children: ReactNode;
}) {
  const {
    account,
    signOut
  } = useSignedInAccount();
  const {
    openAsk
  } = useAskSarahActions();
  const {
    pathname
  } = useLocation();
  const reduce = useReducedMotion();
  const {
    tabs,
    activeTab
  } = useAccountNav();
  const indicator = reduce ? {
    duration: 0
  } : {
    duration: 0.22,
    ease
  };

  // Onderruimte op de telefoon: duimbalk (64px) + "Vraag Sarah" (80px vanaf onder, 44px hoog) + lucht,
  // zodat de laatste knop of regel van een pagina nooit onder de zwevende knop blijft zitten.
  return <div className="mx-auto max-w-content px-5 pb-[calc(9rem+env(safe-area-inset-bottom))] pt-8 sm:px-6 lg:grid lg:grid-cols-12 lg:gap-10 lg:px-10 lg:pb-24 lg:pt-12" data-id="element-5757" data-mp="38eh1ldfl26ru">
      <aside aria-label="Menu van Mijn omgeving" className="hidden lg:col-span-3 lg:block" data-id="element-5758" data-mp="4wytd4vwexqw">
        <div className="sticky top-24" data-id="element-5759" data-mp="3w1grnwtf1yfi">
          <p className="truncate font-display text-[22px] leading-tight text-ink" data-id="element-5760" data-mp="3w0f791qifzl4">Hoi {account.customer.firstName}</p>

          <nav aria-label="Mijn omgeving" className="mt-6" data-id="element-5761" data-mp="3s2zmvjxgx019">
            <ul className="space-y-0.5" data-id="element-5762" data-mp="2kompiy338m8n">
              {tabs.map(item => {
              const active = activeTab === item.to;
              return <li key={item.to} data-id="element-5763" data-mp="2i23v1ounhd3f">
                    <Link to={item.to} aria-current={pathname.replace(/\/+$/, '') === item.to ? 'page' : undefined} className={`relative flex items-center gap-3 rounded-full px-4 py-2.5 text-[15px] transition-colors duration-150 ease-soft ${active ? 'text-ink' : 'text-muted hover:text-ink'}`} data-id="element-5764" data-mp="2i36kdscbr7h8">
                      {active && <motion.span layoutId="account-side" className="absolute inset-0 rounded-full bg-shell ring-1 ring-line" transition={indicator} data-id="element-5765" data-mp="1zbds6052xqak" />}
                      <item.icon className={`relative h-4 w-4 ${active ? 'text-clay' : ''}`} strokeWidth={1.5} aria-hidden="true" data-id="element-5766" data-mp="1pdzkaox829tv" />
                      <span className="relative" data-id="element-5767" data-mp="1ue468ugwfwrh">{item.short}</span>
                    </Link>
                  </li>;
            })}
            </ul>
            <div className="mt-6 border-t border-line pt-4" data-id="element-5768" data-mp="1kevca1m1ai54">
              <button type="button" onClick={signOut} className="flex w-full items-center gap-3 rounded-full px-4 py-2.5 text-[14px] text-muted transition-colors duration-150 ease-soft hover:text-ink" data-id="element-5769" data-mp="1ke3i8v834sgy">
                <LogOutIcon className="h-4 w-4" strokeWidth={1.5} aria-hidden="true" data-id="element-5770" data-mp="1arj16oyaxk3f" />
                Uitloggen
              </button>
            </div>
          </nav>

          <button type="button" onClick={() => openAsk('routine')} aria-haspopup="dialog" className="mt-8 flex w-full items-center gap-3 bg-ink p-4 text-left text-shell transition-colors duration-150 ease-soft hover:bg-clay" data-id="element-5771" data-mp="1kpgpfcg5vgw1">
            <img src={founder.portrait} alt="" className="h-11 w-11 shrink-0 rounded-full object-cover" data-id="element-5772" data-mp="1tjlcrlw36a6t" />
            <span className="min-w-0" data-id="element-5773" data-mp="23fy3qc08y9xv">
              <span className="block text-[14px]" data-id="element-5774" data-mp="23gzrnh2d2qx0">Vraag {founder.firstName}</span>
              <span className="block text-[12px] leading-snug text-shell/70" data-id="element-5775" data-mp="1yhdd94asr4mm">Ze ziet direct je dossier</span>
            </span>
          </button>
        </div>
      </aside>

      <div className="min-w-0 lg:col-span-9" data-id="element-5776" data-mp="2ih572drv1ox4">
        <PaymentIssueBanner data-id="element-5777" data-mp="2mji3gbkpwah8" />
        <VerifyEmailBanner data-id="element-5778" data-mp="2miqkc08b457u" />
        {/*
          Alleen een korte inkomst, geen uitgang. Een uitgang met mode="wait" hield
          het nieuwe onderdeel soms tegen (snel doorklikken): dan bleef het vlak leeg.
         */}
        <motion.div key={pathname} initial={reduce ? false : {
        opacity: 0,
        y: 6
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        duration: 0.18,
        ease
      }} data-id="element-5779" data-mp="1zk3r5xwrq4ee">
          {children}
        </motion.div>
      </div>

      {/* Vraag Sarah, altijd binnen duimbereik op de telefoon (boven de duimbalk van 64px). */}
      <button type="button" onClick={() => openAsk('routine')} aria-haspopup="dialog" className="fixed bottom-[calc(80px+env(safe-area-inset-bottom))] right-[max(1rem,env(safe-area-inset-right))] z-bar flex items-center gap-2 rounded-full bg-ink py-1.5 pl-1.5 pr-4 text-[13px] text-shell shadow-float transition-transform duration-150 ease-soft active:scale-[0.97] lg:hidden" data-id="element-5780" data-mp="2djvc6ilpcl1w">
        <img src={founder.portrait} alt="" className="h-8 w-8 rounded-full object-cover" data-id="element-5781" data-mp="18c28gg4w7fcv" />
        Vraag {founder.firstName}
      </button>

      {/* Duimbalk: vier tabs, gelijk verdeeld. */}
      <nav aria-label="Mijn omgeving" className="fixed inset-x-0 bottom-0 z-bar border-t border-line bg-shell/95 pb-[env(safe-area-inset-bottom)] backdrop-blur-md lg:hidden" data-id="element-5782" data-mp="yn292vmhahy5">
        <ul className="grid grid-cols-4" data-id="element-5783" data-mp="uiuggncwghun">
          {tabs.map(item => {
          const active = activeTab === item.to;
          return <li key={item.to} data-id="element-5784" data-mp="ko2fd9k3zcv4">
                <Link to={item.to} aria-label={item.title} aria-current={pathname.replace(/\/+$/, '') === item.to ? 'page' : undefined} className={`relative flex h-16 flex-col items-center justify-center gap-1 whitespace-nowrap text-[12px] transition-colors duration-150 ease-soft ${active ? 'text-ink' : 'text-muted'}`} data-id="element-5785" data-mp="kp3t5lsiedgj">
                  {active && <motion.span layoutId="account-tab" className="absolute inset-x-6 top-0 h-0.5 rounded-full bg-clay" transition={indicator} data-id="element-5786" data-mp="d3i5pqhd8ojp" />}
                  <item.icon className="h-5 w-5" strokeWidth={1.5} aria-hidden="true" data-id="element-5787" data-mp="3ul1f7fl8970n" />
                  <span aria-hidden="true" data-id="element-5788" data-mp="3cx5jym1nbyo">{item.short}</span>
                </Link>
              </li>;
        })}
        </ul>
      </nav>
    </div>;
}